import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminBrandingSettings,
  type AdminUpdateBrandingRequest,
  adminUpdateBrandingRequestSchema,
} from "@ternary/contracts"
import {
  Avatar,
  Button,
  type Column,
  contrastRatio,
  DEFAULT_BRAND,
  DescriptionList,
  Field,
  IconButton,
  Input,
  NavTabs,
  PageHeader,
  Stepper,
  Table,
  Tabs,
  Textarea,
  ThemeRoot,
  type ThemeTokens,
  ToneIcon,
  themeFromBrand,
  useToast,
} from "@ternary/ui"
import { type CSSProperties, type FormEvent, useEffect, useId, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { Link, useSearchParams } from "react-router-dom"
import {
  type AdminBrandingSave,
  loadAdminAddress,
  loadAdminDomains,
  loadAdminSettings,
  updateAdminBranding,
  updateAdminWelcomeEmail,
} from "../api.js"
import { brandColorsForSave, COLOR_FORMAT_MESSAGE, ColorField } from "../components/color-field.js"
import { DocsLink } from "../components/docs-link.js"
import { FormLoading } from "../components/form-loading.js"
import { ImageUploadField } from "../components/image-upload-field.js"
import {
  initialStandardLegal,
  LegalLinks,
  LINK_MESSAGE,
  legalSaveFields,
} from "../components/legal-links.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { ViewOnlyNotice } from "../components/view-only.js"
import { useWide } from "../use-wide.js"

export type SettingsTab =
  | "account"
  | "address"
  | "branding"
  | "developer"
  | "domains"
  | "email"
  | "team"

const settingsTabs: readonly [SettingsTab, string, string][] = [
  ["branding", "Branding", "/dashboard/settings"],
  ["address", "Address", "/dashboard/settings/address"],
  ["domains", "Domains", "/dashboard/settings/domains"],
  ["email", "Email Settings", "/dashboard/settings/email"],
  ["developer", "Developer", "/dashboard/settings/developer"],
  ["team", "Team", "/dashboard/team"],
  ["account", "Account", "/dashboard/account"],
]

/** The business's custom domains (T15), which Settings → Domains shows and its tab waits for. */
export const adminDomainsQuery = {
  queryFn: ({ signal }: { signal: AbortSignal }) => loadAdminDomains({ signal }),
  queryKey: ["admin-domains"],
}

/** The business's automatic address, which Settings → Address shows and its tab waits for. */
export const adminAddressQuery = {
  queryFn: ({ signal }: { signal: AbortSignal }) => loadAdminAddress({ signal }),
  queryKey: ["admin-address"],
}

/**
 * The Settings title, the docs link and the tabs between the settings pages. The Address tab shows
 * only once admin-api answers an address: without a base domain it answers null, and the tabs are
 * the ones a single-business deployment always had.
 */
export function SettingsHeader({ active }: { active: SettingsTab }) {
  const address = useQuery(adminAddressQuery)
  const domains = useQuery(adminDomainsQuery)
  // T17: Domains once admin-api answers a provider, or the business already has a domain.
  const shown = (tab: SettingsTab) =>
    tab === "address"
      ? Boolean(address.data)
      : tab === "domains"
        ? domains.data?.provider === "managed" || Boolean(domains.data?.domains.length)
        : true
  const tabs = settingsTabs.filter(([tab]) => shown(tab) || tab === active)
  return (
    <div className="admin-stack">
      <PageHeader actions={<DocsLink />} title="Settings" />
      <NavTabs label="Settings">
        {tabs.map(([tab, label, path]) => (
          <Link aria-current={tab === active ? "page" : undefined} key={path} to={path}>
            {label}
          </Link>
        ))}
      </NavTabs>
    </div>
  )
}

const MAX_LINKS = 20

type SidebarLink = AdminUpdateBrandingRequest["sidebarLinks"][number]

// Each row keeps one id for as long as the form is open, so React never remounts a row (and drops
// its focus) while its name or URL is being edited. The ids never leave the form.
type LinkRow = SidebarLink & { id: string }

type BrandingDraft = Pick<
  AdminUpdateBrandingRequest,
  "backgroundUrl" | "groupLogoUrl" | "groupName" | "primaryColor" | "termsUrl"
> & {
  /** The video link as typed; "" is none. */
  backgroundVideoUrl: string
  privacyUrl: string
  sidebarLinks: LinkRow[]
  standardLegal: boolean
}

/**
 * Errors by field: `groupName`, `backgroundVideoUrl`, `primaryColor`, `termsUrl`, `privacyUrl`,
 * or `<row id>.name` and `.url`.
 */
type BrandingErrors = Partial<Record<string, string>>

type BrandingTabId = "identity" | "colours" | "legal" | "links" | "preview"

const VIDEO_LINK_MESSAGE = "Enter a link that starts with https://."

/** The tab that holds the field an error belongs to. */
function tabOf(key: string): BrandingTabId {
  if (key === "groupName" || key === "backgroundVideoUrl") return "identity"
  if (key === "primaryColor") return "colours"
  if (key === "termsUrl" || key === "privacyUrl") return "legal"
  return "links"
}

type BrandColors = NonNullable<ReturnType<typeof brandColorsForSave>>

/**
 * The request Save sends: the text trimmed, and the brand colours from `brandColorsForSave`. An
 * empty privacy or background video link removes the saved one (null), or, with none saved, is
 * left out. With Ternary's standard terms chosen, the saved links stay as they are (G5).
 */
function brandingRequest(
  draft: BrandingDraft,
  colors: BrandColors,
  saved: AdminBrandingSettings,
): AdminBrandingSave {
  const { standardAvailable } = saved.legal
  const legal = legalSaveFields(draft, saved, standardAvailable)
  const video = draft.backgroundVideoUrl.trim()
  // In today's key order: the privacy link first, the terms link after the sidebar links.
  return {
    ...("privacyUrl" in legal ? { privacyUrl: legal.privacyUrl } : {}),
    backgroundUrl: draft.backgroundUrl,
    ...(video
      ? { backgroundVideoUrl: video }
      : saved.backgroundVideoUrl
        ? { backgroundVideoUrl: null }
        : {}),
    groupLogoUrl: draft.groupLogoUrl,
    groupName: draft.groupName.trim(),
    ...colors,
    sidebarLinks: draft.sidebarLinks.map(({ name, url }) => ({
      name: name.trim(),
      url: url.trim(),
    })),
    termsUrl: legal.termsUrl,
    // Only while they are available: admin-api refuses a saved true once they are off (M10a).
    ...(standardAvailable ? { standardLegal: draft.standardLegal } : {}),
  }
}

/** The default theme's colours: a stand-in, so a bad colour does not hide the other fields' errors. */
const STAND_IN_COLORS: BrandColors = {
  primaryColor: DEFAULT_BRAND,
  secondaryColor: themeFromBrand(DEFAULT_BRAND).light.onPrimary,
}

/**
 * What the contract would reject: each field's error with its message, and `problem`, the message
 * for an issue no field shows (such as more than 20 links in legacy data), or null.
 */
function validateBranding(
  draft: BrandingDraft,
  saved: AdminBrandingSettings,
): {
  errors: BrandingErrors
  problem: string | null
} {
  const errors: BrandingErrors = {}
  let problem: string | null = null
  const colors = brandColorsForSave(draft.primaryColor)
  if (!colors) errors.primaryColor = COLOR_FORMAT_MESSAGE
  const request = brandingRequest(draft, colors ?? STAND_IN_COLORS, saved)
  const parsed = adminUpdateBrandingRequestSchema.safeParse(request)
  for (const { path, message } of parsed.error?.issues ?? []) {
    const [field, index, part] = path
    const row = typeof index === "number" ? draft.sidebarLinks[index] : undefined
    if (field === "groupName") errors.groupName = "Enter your company name."
    else if (field === "backgroundVideoUrl") errors.backgroundVideoUrl = VIDEO_LINK_MESSAGE
    else if (field === "termsUrl" || field === "privacyUrl") errors[field] = LINK_MESSAGE
    else if (field === "sidebarLinks" && row && (part === "name" || part === "url")) {
      errors[`${row.id}.${part}`] = part === "name" ? "Enter a name for this link." : LINK_MESSAGE
    } else if (field === "sidebarLinks" && path.length === 1) {
      problem ??= `Remove links until there are ${MAX_LINKS} or fewer.`
    } else {
      problem ??= `These settings can't be saved: ${message}`
    }
  }
  return { errors, problem }
}

/** The ratio rounded down to one decimal, so a pass never shows as a higher ratio than it is. */
function ratioText(ratio: number) {
  return `${(Math.floor(ratio * 10) / 10).toFixed(1)}:1`
}

/**
 * One generated colour: an "Aa" sample in it, its hex and its contrast ratio. A button sample is
 * the on-primary text on the primary; a link sample is the primary text on the surface.
 */
function Swatch({ tokens, kind }: { tokens: ThemeTokens; kind: "button" | "link" }) {
  const fill = kind === "button" ? tokens.primary : tokens.surface
  const ink = kind === "button" ? tokens.onPrimary : tokens.primaryText
  // The sample shows the mode's own tokens on the admin page, through custom properties that
  // app.css reads, as ThemeRoot does.
  const style = {
    "--admin-swatch-edge": tokens.border,
    "--admin-swatch-fill": fill,
    "--admin-swatch-ink": ink,
  } as CSSProperties
  return (
    <span className="admin-swatch-cell">
      <span aria-hidden="true" className={`admin-swatch admin-swatch-${kind}`} style={style}>
        Aa
      </span>
      <code className="admin-mono">{kind === "button" ? tokens.primary : tokens.primaryText}</code>
      <span className="admin-note">{ratioText(contrastRatio(ink, fill))}</span>
    </span>
  )
}

type Mode = "light" | "dark"

function paletteColumns(theme: ReturnType<typeof themeFromBrand>): Column<Mode>[] {
  return [
    { key: "mode", header: "Mode", cell: (mode) => (mode === "light" ? "Light" : "Dark") },
    {
      key: "button",
      header: "Buttons",
      cell: (mode) => <Swatch kind="button" tokens={theme[mode]} />,
    },
    {
      key: "link",
      header: "Links and focus",
      cell: (mode) => <Swatch kind="link" tokens={theme[mode]} />,
    },
  ]
}

const PREVIEW_STEPS = [
  { id: "details", label: "Details" },
  { id: "discord", label: "Discord" },
  { id: "join", label: "Join" },
]

/**
 * The start of the signup page in the draft brand: a picture, so it is inert and its controls are
 * neither focusable nor read out. The caption names it.
 */
function BrandPreview({ brand, logo, name }: { brand: string; logo: string | null; name: string }) {
  const captionId = useId()
  return (
    <figure aria-labelledby={captionId} className="admin-brand-preview">
      <figcaption className="admin-note" id={captionId}>
        Preview of your signup page
      </figcaption>
      <ThemeRoot brand={brand} className="admin-brand-preview-frame" inert>
        <div className="admin-brand-preview-bar">
          <Avatar name={name} size="sm" src={logo} />
          <span>{name}</span>
        </div>
        <div className="admin-stack admin-brand-preview-body">
          <p className="admin-brand-preview-title">Join {name}</p>
          <Stepper currentId="details" label="Signup steps" steps={PREVIEW_STEPS} />
          <Field label="First name">
            <Input readOnly value="Maya" />
          </Field>
          <Button>Continue</Button>
          <span className="admin-brand-preview-link">Terms of Service</span>
        </div>
      </ThemeRoot>
    </figure>
  )
}

function BrandingForm({
  id,
  onSave,
  onProblem,
  onBusyChange,
  onEdit,
  settings,
}: {
  id: string
  onSave(settings: AdminBrandingSave): void
  /** Any edit or tab change: the last save's notice no longer describes what is on screen. */
  onEdit(): void
  /** A Save blocked by an issue no field shows, with its message; "" when nothing blocks it so. */
  onProblem(message: string): void
  /** Whether the logo or the background is uploading, so the page can hold Save meanwhile. */
  onBusyChange(kind: "logo" | "background", busy: boolean): void
  settings: AdminBrandingSettings
}) {
  const fieldId = useId()
  const formRef = useRef<HTMLFormElement>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  // From 1024px the preview sits beside the form; below it, it is a tab.
  const wide = useWide()
  const [params, setParams] = useSearchParams()
  // The tab lives here, so a blocked Save can open it at once; the URL keeps it for a reload.
  const [tab, setTabState] = useState(() => params.get("tab") ?? "identity")
  const [draft, setDraft] = useState<BrandingDraft>(() => ({
    backgroundUrl: settings.backgroundUrl,
    backgroundVideoUrl: settings.backgroundVideoUrl ?? "",
    groupLogoUrl: settings.groupLogoUrl,
    groupName: settings.groupName,
    primaryColor: settings.primaryColor,
    privacyUrl: settings.privacyUrl,
    sidebarLinks: settings.sidebarLinks.map((link) => ({ ...link, id: crypto.randomUUID() })),
    standardLegal: initialStandardLegal(settings, settings.legal.standardAvailable),
    termsUrl: settings.termsUrl,
  }))
  const [errors, setErrors] = useState<BrandingErrors>({})
  // The palette and the preview keep the last colour typed in full while the field holds part of
  // one.
  const [brand, setBrand] = useState(
    () => brandColorsForSave(settings.primaryColor)?.primaryColor ?? DEFAULT_BRAND,
  )
  const typed = brandColorsForSave(draft.primaryColor)?.primaryColor
  if (typed && typed !== brand) setBrand(typed)
  const locked = !settings.writable

  function setTab(next: string) {
    onEdit()
    setTabState(next)
    setParams(
      (current) => {
        current.set("tab", next)
        return current
      },
      { replace: true },
    )
  }

  // Editing a field takes its error away until the next Save checks it again.
  function change(next: BrandingDraft, field?: string) {
    onEdit()
    setDraft(next)
    if (field && errors[field]) {
      const { [field]: _, ...rest } = errors
      setErrors(rest)
    }
  }

  function setLink(index: number, link: LinkRow, field: string) {
    const sidebarLinks = [...draft.sidebarLinks]
    sidebarLinks[index] = link
    change({ ...draft, sidebarLinks }, field)
  }

  function addLink() {
    onEdit()
    const row = { id: crypto.randomUUID(), name: "", url: "" }
    // Rendered now, so the new row's name can take focus.
    flushSync(() => setDraft({ ...draft, sidebarLinks: [...draft.sidebarLinks, row] }))
    document.getElementById(`${fieldId}-${row.id}-name`)?.focus()
  }

  function removeLink(row: LinkRow) {
    onEdit()
    const { [`${row.id}.name`]: _name, [`${row.id}.url`]: _url, ...rest } = errors
    flushSync(() => {
      setErrors(rest)
      setDraft({ ...draft, sidebarLinks: draft.sidebarLinks.filter((link) => link.id !== row.id) })
    })
    // The removed row's button is gone, so focus goes on to Add link.
    addRef.current?.focus()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const { errors: found, problem } = validateBranding(draft, settings)
    const first = (["identity", "colours", "legal", "links"] as const).find((panel) =>
      Object.keys(found).some((key) => tabOf(key) === panel),
    )
    onProblem(problem ?? "")
    // Rendered now, with the first error's tab open, so its field can take focus.
    flushSync(() => {
      setErrors(found)
      if (first) setTab(first)
    })
    if (first) {
      formRef.current
        ?.querySelector<HTMLElement>('[role="tabpanel"]:not([hidden]) [aria-invalid="true"]')
        ?.focus()
      return
    }
    const colors = brandColorsForSave(draft.primaryColor)
    if (problem || !colors) return
    onSave(brandingRequest(draft, colors, settings))
  }

  const { standardAvailable } = settings.legal
  // Side by side where they fit, stacked on a phone. No privacy hint, as before the links had a tab
  // of their own.
  const legalLinks = (
    <LegalLinks
      companyName={draft.groupName.trim() || settings.groupName}
      disabled={locked}
      draft={draft}
      errors={{ privacyUrl: errors.privacyUrl, termsUrl: errors.termsUrl }}
      onChange={(patch) => change({ ...draft, ...patch }, Object.keys(patch)[0])}
      standardAvailable={standardAvailable}
    />
  )
  const theme = themeFromBrand(brand)
  const preview = (
    <BrandPreview
      brand={brand}
      logo={draft.groupLogoUrl}
      name={draft.groupName.trim() || settings.groupName}
    />
  )
  const tabs = [
    {
      id: "identity",
      label: "Identity",
      content: (
        <div className="admin-stack">
          {/* Read-only, the name is a value, as the colour and the images are. */}
          {locked ? (
            <DescriptionList items={[{ term: "Company name", value: draft.groupName }]} />
          ) : (
            <Field error={errors.groupName} label="Company name" required>
              <Input
                autoComplete="organization"
                maxLength={120}
                onChange={(event) =>
                  change({ ...draft, groupName: event.currentTarget.value }, "groupName")
                }
                value={draft.groupName}
              />
            </Field>
          )}
          {/* An upload ends after later edits, so it updates the draft as it is by then. */}
          <ImageUploadField
            disabled={locked}
            kind="logo"
            label="Logo"
            onBusyChange={(busy) => onBusyChange("logo", busy)}
            onChange={(groupLogoUrl) => {
              onEdit()
              setDraft((current) => ({ ...current, groupLogoUrl }))
            }}
            uploadsAvailable={settings.assetUploadsAvailable}
            value={draft.groupLogoUrl}
          />
          {/*
           * The background and the video that plays over it, side by side where they fit, so
           * Identity still fits the screen. The video is a link to a file the business hosts:
           * there is no video upload.
           */}
          <div className="admin-background-pair">
            <ImageUploadField
              disabled={locked}
              kind="background"
              label="Checkout background"
              onBusyChange={(busy) => onBusyChange("background", busy)}
              onChange={(backgroundUrl) => {
                onEdit()
                setDraft((current) => ({ ...current, backgroundUrl }))
              }}
              uploadsAvailable={settings.assetUploadsAvailable}
              value={draft.backgroundUrl}
            />
            <Field
              error={errors.backgroundVideoUrl}
              hint="An MP4 or WebM file, played muted on a loop."
              label="Background video link"
            >
              <Input
                disabled={locked}
                inputMode="url"
                maxLength={2_048}
                onChange={(event) =>
                  change(
                    { ...draft, backgroundVideoUrl: event.currentTarget.value },
                    "backgroundVideoUrl",
                  )
                }
                type="url"
                value={draft.backgroundVideoUrl}
              />
            </Field>
          </div>
          {settings.assetUploadsAvailable ? null : (
            <p className="admin-note">
              Image uploads are unavailable until public asset storage is connected.
            </p>
          )}
        </div>
      ),
    },
    {
      id: "colours",
      label: "Colours",
      content: (
        <div className="admin-stack">
          <ColorField
            disabled={locked}
            error={errors.primaryColor}
            label="Brand colour"
            onChange={(primaryColor) => change({ ...draft, primaryColor }, "primaryColor")}
            value={draft.primaryColor}
          />
          <Table
            caption="Generated colours"
            columns={paletteColumns(theme)}
            rowKey={(mode) => mode}
            rows={["light", "dark"] satisfies Mode[]}
          />
          <p className="admin-note admin-table-note">
            AA means most people can read it. Higher is better.
          </p>
        </div>
      ),
    },
    // The terms and privacy links (with the choice of Ternary's standard terms while it is
    // available, G5), their advice and errors: a tab of their own, so Links, with the member menu
    // links, still fits a phone once each pair of fields stacks.
    { id: "legal", label: "Legal", content: <div className="admin-stack">{legalLinks}</div> },
    {
      id: "links",
      label: "Links",
      content: (
        <div className="admin-stack">
          <fieldset aria-labelledby={`${fieldId}-links`} className="admin-links">
            {/* The heading row holds Add link, so the list needs no row of its own below it. */}
            <div className="admin-links-head">
              <h2 className="admin-heading" id={`${fieldId}-links`}>
                Member menu links
              </h2>
              <span className="admin-note">
                {draft.sidebarLinks.length} of {MAX_LINKS}
              </span>
              {locked ? null : (
                <Button
                  disabled={draft.sidebarLinks.length >= MAX_LINKS}
                  onClick={addLink}
                  ref={addRef}
                  size="sm"
                  variant="secondary"
                >
                  Add link
                </Button>
              )}
            </div>
            {draft.sidebarLinks.length > 0 ? (
              // The column heads, drawn once: every row's own labels name its fields to a screen
              // reader, so the heads are hidden from it.
              <div aria-hidden="true" className="admin-link-row admin-link-row-head">
                <span className="tny-label">Name</span>
                <span className="tny-label">URL</span>
              </div>
            ) : null}
            {draft.sidebarLinks.map((link, index) => (
              <div className="admin-link-row" key={link.id}>
                <Field error={errors[`${link.id}.name`]} label={`Link ${index + 1} name`}>
                  <Input
                    disabled={locked}
                    id={`${fieldId}-${link.id}-name`}
                    maxLength={80}
                    onChange={(event) =>
                      setLink(
                        index,
                        { ...link, name: event.currentTarget.value },
                        `${link.id}.name`,
                      )
                    }
                    value={link.name}
                  />
                </Field>
                <Field error={errors[`${link.id}.url`]} label={`Link ${index + 1} URL`}>
                  <Input
                    disabled={locked}
                    inputMode="url"
                    onChange={(event) =>
                      setLink(index, { ...link, url: event.currentTarget.value }, `${link.id}.url`)
                    }
                    type="url"
                    value={link.url}
                  />
                </Field>
                {locked ? null : (
                  <IconButton
                    className="admin-link-remove"
                    icon={<NavIcon name="trash" />}
                    label={`Remove link ${index + 1}`}
                    onClick={() => removeLink(link)}
                  />
                )}
              </div>
            ))}
          </fieldset>
        </div>
      ),
    },
    ...(wide ? [] : [{ id: "preview", label: "Preview", content: preview }]),
  ]

  return (
    <form
      aria-label="Branding"
      className={wide ? "admin-branding admin-branding-wide" : "admin-branding"}
      id={id}
      noValidate
      onSubmit={submit}
      ref={formRef}
    >
      <Tabs label="Branding" onChange={setTab} tabs={tabs} value={tab} />
      {wide ? preview : null}
    </form>
  )
}

/** The welcome email. Its Save is in the page's Save bar, outside this keyed form. */
function EmailForm({
  id,
  onEdit,
  onSave,
  settings,
}: {
  id: string
  /** An edit: the last save's notice no longer describes the field. */
  onEdit(): void
  onSave(welcomeEmail: string): void
  settings: AdminBrandingSettings
}) {
  const [welcomeEmail, setWelcomeEmail] = useState(settings.welcomeEmail)

  return (
    <form
      aria-label="Welcome email"
      className="admin-stack admin-page-narrow"
      id={id}
      onSubmit={(event) => {
        event.preventDefault()
        onSave(welcomeEmail)
      }}
    >
      <Field
        hint="Sent to a customer once they buy a membership."
        label="Welcome email"
        required={settings.writable}
      >
        <Textarea
          disabled={!settings.writable}
          maxLength={20_000}
          onChange={(event) => {
            onEdit()
            setWelcomeEmail(event.target.value)
          }}
          rows={12}
          value={welcomeEmail}
        />
      </Field>
    </form>
  )
}

export function AdminSettingsPage({ active = "branding" }: { active?: "branding" | "email" }) {
  const formId = useId()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  // Branding and Email share this page (the route keeps it mounted between them): a tab change
  // drops the other tab's save messages.
  const [shown, setShown] = useState(active)
  if (shown !== active) {
    setShown(active)
    setNotice("")
    setActionError("")
  }
  // The tab on screen when a save answers: a save that ends after a move to the other tab confirms
  // nothing there. Null once the admin has left Settings (another route), so a failure that
  // answers then is a toast, which AdminRoot still shows.
  const activeRef = useRef<typeof active | null>(active)
  useEffect(() => {
    activeRef.current = active
    return () => {
      activeRef.current = null
    }
  }, [active])
  const [uploading, setUploading] = useState({ background: false, logo: false })
  const query = useQuery({ queryFn: loadAdminSettings, queryKey: ["admin-settings"] })

  const branding = useMutation({
    mutationFn: updateAdminBranding,
    onMutate() {
      setNotice("")
      setActionError("")
    },
    async onSuccess(data) {
      if (activeRef.current === "branding") setNotice("Branding settings saved.")
      queryClient.setQueryData(["admin-settings"], data)
      // The terms and privacy are on the dashboard's setup checklist (G7).
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      // The product catalog's legal.ready follows Branding, and the wizard's Basics reads it (G5).
      // Only while the standard terms are available, so with the setting off a save is today's.
      if (data.legal.standardAvailable) {
        await queryClient.invalidateQueries({ queryKey: ["admin-products"] })
      }
      await queryClient.invalidateQueries({ queryKey: ["admin-session"] })
    },
    onError(error) {
      const message = error instanceof Error ? error.message : "Branding could not be saved."
      // A failure that answers after a move to Email leaves Email's save bar alone, and says so
      // in a toast, so it is never lost.
      if (activeRef.current !== "branding") {
        toast({ message: `Branding settings weren't saved: ${message}`, tone: "error" })
        return
      }
      setActionError(message)
    },
  })
  const email = useMutation({
    mutationFn: updateAdminWelcomeEmail,
    // A save that answers after a move to Branding leaves Branding's own messages alone.
    onSuccess(data) {
      if (activeRef.current === "email") {
        setActionError("")
        setNotice("Email settings saved.")
      }
      // Only the welcome email: the rest of the answer is the branding as it stood when this save
      // started, which a Branding save since then would have changed.
      queryClient.setQueryData<AdminBrandingSettings>(["admin-settings"], (current) =>
        current ? { ...current, welcomeEmail: data.welcomeEmail } : data,
      )
    },
    onError(error) {
      const message = error instanceof Error ? error.message : "Email settings could not save."
      // As Branding's: a late failure after a move to Branding is a toast there.
      if (activeRef.current !== "email") {
        toast({ message: `Email settings weren't saved: ${message}`, tone: "error" })
        return
      }
      setNotice("")
      setActionError(message)
    },
  })

  if (query.isPending || query.isError) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <SettingsHeader active={active} />
        {query.isPending ? (
          <FormLoading fields={active === "email" ? 1 : 3} label="Loading settings…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message={
              active === "email"
                ? "We couldn't load your email settings"
                : "We couldn't load your branding settings"
            }
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }

  /** Outside the keyed form, so the messages survive the reset that a save causes. */
  const saveBar = ({ disabled = false, pending }: { disabled?: boolean; pending: boolean }) =>
    query.data.writable ? (
      <div className="admin-save-bar">
        {actionError ? (
          <p className="admin-save-message tny-error" role="alert">
            {actionError}
          </p>
        ) : null}
        <p
          className={notice ? "admin-save-message is-success" : "admin-save-message"}
          role="status"
        >
          {notice ? (
            <>
              <ToneIcon tone="success" />
              {notice}
            </>
          ) : null}
        </p>
        <Button disabled={disabled} form={formId} loading={pending} type="submit">
          Save
        </Button>
      </div>
    ) : null

  if (active === "email") {
    return (
      <section className="admin-stack admin-page">
        <SettingsHeader active="email" />
        {query.data.writable ? null : <ViewOnlyNotice />}
        <EmailForm
          id={formId}
          key={query.data.welcomeEmail}
          onEdit={() => setNotice("")}
          onSave={(welcomeEmail) => email.mutate({ welcomeEmail })}
          settings={query.data}
        />
        {saveBar({ pending: email.isPending })}
      </section>
    )
  }

  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active={active} />
      {query.data.writable ? null : <ViewOnlyNotice />}
      {/* Keyed by the saved branding, so a save or a refetch that changes it resets the draft. The
          welcome email is left out: an Email save that answers here keeps the draft. */}
      <BrandingForm
        id={formId}
        key={JSON.stringify({ ...query.data, welcomeEmail: undefined })}
        onBusyChange={(kind, busy) => setUploading((current) => ({ ...current, [kind]: busy }))}
        onEdit={() => setNotice("")}
        onProblem={(message) => {
          if (message) setNotice("")
          setActionError(message)
        }}
        onSave={(settings) => branding.mutate(settings)}
        settings={query.data}
      />
      {/* Disabled while an image uploads, which also stops Enter submitting the form. */}
      {saveBar({ disabled: uploading.logo || uploading.background, pending: branding.isPending })}
    </section>
  )
}
